import { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSErrorButton from "views/system-design/DSErrorButton";
import DSIconButton from "views/system-design/DSIconButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSTextField from "views/system-design/DSTextField";
import DSPaper from "views/system-design/DSPaper";
import DSCheckBox from "views/system-design/DSCheckBox";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSChip from "views/system-design/DSChip";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSFormControl from "views/system-design/DSFormControl";
import DSInputLabel from "views/system-design/DSInputLabel";
import { Plan, PlanStatus, CreatePlanDto } from "api/plan";
import {
  usePlansQuery,
  useCreatePlanMutation,
  useUpdatePlanStatusMutation,
  useUpdatePlanContentMutation,
} from "../hooks/usePlans";

const emptyForm = (): CreatePlanDto => ({
  title: "",
  price: 0,
  upfrontAmount: 0,
  revSharePercentage: 0,
  isAdvanced: false,
  explanation: "",
  status: PlanStatus.Unlisted,
});

const PlanCard = ({
  plan,
  onStatusChange,
  onDelete,
  onContentSave,
  isUpdating,
}: {
  plan: Plan;
  onStatusChange: (id: string, status: PlanStatus) => void;
  onDelete: (id: string) => void;
  onContentSave: (
    id: string,
    data: { title?: string; explanation?: string },
  ) => void;
  isUpdating: boolean;
}) => {
  const [editTitle, setEditTitle] = useState(plan.title ?? "");
  const [editExplanation, setEditExplanation] = useState(
    plan.explanation ?? "",
  );

  useEffect(() => {
    setEditTitle(plan.title ?? "");
    setEditExplanation(plan.explanation ?? "");
  }, [plan.id]);

  const hasChanges =
    editTitle !== (plan.title ?? "") ||
    editExplanation !== (plan.explanation ?? "");

  return (
    <DSPaper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Main row */}
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <DSBox sx={{ flex: 1, display: "flex", gap: 4, alignItems: "center" }}>
          <DSBox>
            <DSTypography variant="caption" color="text.secondary">
              Price
            </DSTypography>
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSTypography fontWeight="bold">
                ${plan.price.toFixed(2)}
              </DSTypography>
              {plan.compareAtPrice != null && (
                <DSTypography
                  color="text.secondary"
                  sx={{ textDecoration: "line-through" }}
                >
                  ${plan.compareAtPrice.toFixed(2)}
                </DSTypography>
              )}
            </DSBox>
          </DSBox>
          <DSBox>
            <DSTypography variant="caption" color="text.secondary">
              Upfront
            </DSTypography>
            <DSTypography fontWeight="bold">
              {plan.upfrontAmount > 0 ? `$${plan.upfrontAmount}` : "None"}
            </DSTypography>
          </DSBox>
          <DSBox>
            <DSTypography variant="caption" color="text.secondary">
              Rev Share
            </DSTypography>
            <DSTypography fontWeight="bold">
              {plan.revSharePercentage > 0
                ? `${plan.revSharePercentage}%`
                : "None"}
            </DSTypography>
          </DSBox>
          {plan.isAdvanced && (
            <DSChip label="Advanced" size="small" color="warning" />
          )}
        </DSBox>
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DSChip
            label={plan.status}
            size="small"
            color={plan.status === PlanStatus.Active ? "success" : "default"}
            onClick={() =>
              onStatusChange(
                plan.id,
                plan.status === PlanStatus.Active
                  ? PlanStatus.Unlisted
                  : PlanStatus.Active,
              )
            }
            disabled={isUpdating}
            sx={{ cursor: "pointer" }}
          />
          <DSIconButton
            onClick={() => onDelete(plan.id)}
            disabled={isUpdating}
            size="small"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </DSIconButton>
        </DSBox>
      </DSBox>

      {/* Editable title + explanation */}
      <DSBox sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <DSTextField
          label="Title"
          size="small"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          sx={{ flex: 1 }}
        />
        <DSTextField
          label="Explanation"
          size="small"
          value={editExplanation}
          onChange={(e) => setEditExplanation(e.target.value)}
          multiline
          rows={1}
          sx={{ flex: 2 }}
        />
        {hasChanges && (
          <DSPrimaryButton
            size="small"
            onClick={() =>
              onContentSave(plan.id, {
                title: editTitle,
                explanation: editExplanation,
              })
            }
            disabled={isUpdating}
            sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
          >
            Save
          </DSPrimaryButton>
        )}
      </DSBox>

      {/* ID */}
      <DSTypography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: "monospace" }}
      >
        {plan.id}
      </DSTypography>
    </DSPaper>
  );
};

type PendingAction =
  | { type: "delete"; planId: string }
  | { type: "status"; planId: string; newStatus: PlanStatus };

const StudioPlanManager = () => {
  const { data: plans, isPending: isLoading, error } = usePlansQuery();
  const { mutate: createPlan, isPending: isCreating } = useCreatePlanMutation();
  const { mutate: updateStatus, isPending: isUpdating } =
    useUpdatePlanStatusMutation();
  const { mutate: updateContent } = useUpdatePlanContentMutation();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [form, setForm] = useState<CreatePlanDto>(emptyForm());
  const [createError, setCreateError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  const handleOpenCreate = () => {
    setForm(emptyForm());
    setCreateError(null);
    setIsCreateDialogOpen(true);
  };

  const handleCreate = () => {
    if (form.isAdvanced && form.upfrontAmount <= 0) {
      setCreateError(
        "Advanced plans must have an upfront amount greater than 0.",
      );
      return;
    }
    // Shopify only draws the strikethrough when compare-at is above the price.
    if (form.compareAtPrice != null && form.compareAtPrice <= form.price) {
      setCreateError(
        "Compare-at price must be higher than the price, or left empty.",
      );
      return;
    }
    createPlan(form, {
      onSuccess: () => setIsCreateDialogOpen(false),
      onError: () => setCreateError("Failed to create plan. Please try again."),
    });
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;
    if (pendingAction.type === "delete") {
      updateStatus(
        { planId: pendingAction.planId, status: PlanStatus.Deleted },
        { onSettled: () => setPendingAction(null) },
      );
    } else {
      updateStatus(
        { planId: pendingAction.planId, status: pendingAction.newStatus },
        { onSettled: () => setPendingAction(null) },
      );
    }
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox sx={{ p: 3 }}>
        <DSTypography color="error">Failed to load plans.</DSTypography>
      </DSBox>
    );
  }

  const activePlans =
    plans?.filter((p) => p.status !== PlanStatus.Deleted) ?? [];

  return (
    <DSBox sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DSTypography variant="h6">Monetization Plans</DSTypography>
        <DSPrimaryButton startIcon={<AddIcon />} onClick={handleOpenCreate}>
          New Plan
        </DSPrimaryButton>
      </DSBox>

      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {activePlans.length === 0 && (
          <DSTypography
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            No plans yet. Click "New Plan" to create one.
          </DSTypography>
        )}
        {activePlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onStatusChange={(planId, newStatus) =>
              setPendingAction({ type: "status", planId, newStatus })
            }
            onDelete={(planId) => setPendingAction({ type: "delete", planId })}
            onContentSave={(planId, data) => updateContent({ planId, data })}
            isUpdating={isUpdating}
          />
        ))}
      </DSBox>

      {/* Confirmation dialog */}
      <DSDialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>
          {pendingAction?.type === "delete"
            ? "Delete Plan"
            : "Change Plan Status"}
        </DSDialogTitle>
        <DSDialogContent sx={{ pt: "12px !important" }}>
          <DSTypography>
            {pendingAction?.type === "delete"
              ? "Are you sure you want to delete this plan? This action cannot be undone."
              : `Are you sure you want to set this plan to ${pendingAction?.type === "status" ? pendingAction.newStatus : ""}?`}
          </DSTypography>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setPendingAction(null)}>
            Cancel
          </DSSecondaryButton>
          <DSErrorButton onClick={handleConfirmAction} disabled={isUpdating}>
            {isUpdating ? "..." : "Confirm"}
          </DSErrorButton>
        </DSDialogActions>
      </DSDialog>

      {/* Create dialog */}
      <DSDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>New Plan</DSDialogTitle>
        <DSDialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pt: "20px !important",
          }}
        >
          <DSTextField
            label="Title (optional)"
            value={form.title ?? ""}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
            placeholder="e.g. Hybrid"
          />
          <DSTextField
            label="Price per sale ($)"
            type="number"
            value={form.price}
            onChange={(e) =>
              setForm((f) => ({ ...f, price: Number(e.target.value) }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <DSTextField
            label="Compare-at price ($)"
            helperText="Optional, must be higher than the price. Shown struck through on the product page."
            type="number"
            value={form.compareAtPrice ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                compareAtPrice:
                  e.target.value === "" ? undefined : Number(e.target.value),
              }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <DSTextField
            label="Upfront amount ($)"
            helperText="0 = no upfront"
            type="number"
            value={form.upfrontAmount}
            onChange={(e) =>
              setForm((f) => ({ ...f, upfrontAmount: Number(e.target.value) }))
            }
            fullWidth
            inputProps={{ min: 0 }}
          />
          <DSTextField
            label="Rev share %"
            helperText="0 = no rev share"
            type="number"
            value={form.revSharePercentage}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                revSharePercentage: Number(e.target.value),
              }))
            }
            fullWidth
            inputProps={{ min: 0, max: 100 }}
          />
          <DSTextField
            label="Explanation (optional)"
            value={form.explanation ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, explanation: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
            placeholder="e.g. Revenue share begins after the advance is recouped."
          />
          <DSFormControl fullWidth size="small">
            <DSInputLabel>Status</DSInputLabel>
            <DSSelect
              label="Status"
              value={form.status ?? PlanStatus.Unlisted}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as PlanStatus }))
              }
            >
              <DSMenuItem value={PlanStatus.Active}>Active</DSMenuItem>
              <DSMenuItem value={PlanStatus.Unlisted}>Unlisted</DSMenuItem>
            </DSSelect>
          </DSFormControl>
          <DSFormControlLabel
            control={
              <DSCheckBox
                checked={form.isAdvanced}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isAdvanced: e.target.checked }))
                }
              />
            }
            label="Advanced (upfront must be recouped before rev share starts)"
          />
          {createError && (
            <DSTypography color="error" variant="caption">
              {createError}
            </DSTypography>
          )}
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setIsCreateDialogOpen(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton onClick={handleCreate} disabled={isCreating}>
            {isCreating ? "Creating..." : "Create"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
    </DSBox>
  );
};

export default StudioPlanManager;
